import React, { useMemo, useState } from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { DeleteOutline as UndoIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme/tokens';
import { formatMoney, formatMoneyCents } from '../../theme/chart';
import { parseLocalDate } from '../../utils/bills';
import { GoalContribution } from '../../services/goalApi';
import { SectionLabel } from './goalUi';

interface Props {
  trail: GoalContribution[];
  color: string;
  currency: string;
  /** in a group, each entry names who saved it */
  showPeople: boolean;
  canAdjust: boolean;
  onAdjust: () => void;
  canUndo: (entry: GoalContribution) => boolean;
  onUndo: (entry: GoalContribution) => void;
}

/** Entries shown before "see all". */
const SHOWN = 6;

const ADJUSTMENT_NODE = '#9AA8AD';

/**
 * A goal's trail as a timeline: deposits in the goal's color, balance
 * adjustments in grey and marked as outside the deposits, since they never
 * count towards the month or the pace.
 */
export const GoalHistory: React.FC<Props> = ({ trail, color, currency, showPeople, canAdjust, onAdjust, canUndo, onUndo }) => {
  const { t, i18n } = useTranslation('planning');
  const locale = i18n.language;
  const [showAll, setShowAll] = useState(false);
  const thisYear = new Date().getFullYear();

  const money = (value: number) =>
    Number.isInteger(value) ? formatMoney(value, currency, locale) : formatMoneyCents(value, currency, locale);
  const day = (iso: string) => {
    const date = parseLocalDate(iso);
    return date.toLocaleDateString(locale, date.getFullYear() === thisYear
      ? { day: 'numeric', month: 'long' }
      : { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Newest first, whatever order the trail came in.
  const sorted = useMemo(
    () => [...trail].sort((a, b) => b.date.localeCompare(a.date) || (b.createdAt ?? '').localeCompare(a.createdAt ?? '')),
    [trail],
  );
  const entries = showAll ? sorted : sorted.slice(0, SHOWN);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      <SectionLabel
        aside={
          canAdjust ? (
            <Button variant='text' size='small' onClick={onAdjust} sx={{ minHeight: 32, px: 1, mr: -1, fontSize: 13, fontWeight: 600 }}>
              {t('goals.adjust.action')}
            </Button>
          ) : undefined
        }
      >
        {t('goals.history.title')}
      </SectionLabel>

      {trail.length === 0 ? (
        <Box component='p' sx={{ m: 0, fontSize: 13.5, color: tokens.ink3 }}>{t('goals.noContributions')}</Box>
      ) : (
        <Box component='ol' aria-label={t('goals.history.title') as string} sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {entries.map((entry, index) => {
            const isAdjustment = entry.kind === 'adjustment';
            const amount = Number(entry.amount) || 0;
            const sign = amount < 0 ? '−' : '+';
            const details = [
              day(entry.date),
              showPeople && entry.user ? entry.user.firstName : null,
              isAdjustment ? entry.note || null : null,
              isAdjustment ? t('goals.history.outsideDeposits') : null,
            ].filter(Boolean);
            const last = index === entries.length - 1;
            return (
              <Box
                component='li'
                key={entry.id}
                sx={{
                  position: 'relative',
                  display: 'grid',
                  gridTemplateColumns: '18px minmax(0, 1fr) auto',
                  columnGap: 1.5,
                  alignItems: 'start',
                  pb: last ? 0 : 1.75,
                  '&::before': last
                    ? undefined
                    : { content: '""', position: 'absolute', left: 8, top: 18, bottom: 0, width: '2px', bgcolor: tokens.line },
                }}
              >
                <Box
                  component='span'
                  aria-hidden
                  sx={{
                    width: 18,
                    height: 18,
                    mt: '1px',
                    borderRadius: '50%',
                    boxSizing: 'border-box',
                    border: `4px solid ${tokens.surface}`,
                    boxShadow: `0 0 0 1px ${tokens.line}`,
                    bgcolor: isAdjustment ? ADJUSTMENT_NODE : color,
                  }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Box component='strong' sx={{ fontSize: 14 }}>
                    {isAdjustment ? t('goals.history.adjustment') : t('goals.history.deposit')}
                  </Box>
                  <Box component='span' sx={{ fontSize: 12.5, color: tokens.ink3 }}>{details.join(' · ')}</Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <Box component='strong' sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {`${sign}${money(Math.abs(amount))}`}
                  </Box>
                  {canUndo(entry) && (
                    <IconButton size='small' aria-label={t('goals.undoContribution') as string} onClick={() => onUndo(entry)} sx={{ color: tokens.ink3, mr: -0.75 }}>
                      <UndoIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {trail.length > SHOWN && (
        <Button variant='text' size='small' onClick={() => setShowAll((value) => !value)} sx={{ alignSelf: 'flex-start', minHeight: 32, px: 1, ml: -1, fontSize: 13, fontWeight: 600 }}>
          {showAll ? t('goals.history.showLess') : t('goals.history.showAll', { count: trail.length })}
        </Button>
      )}
    </Box>
  );
};
